'use client'

import React, { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  Bot, User, Sparkles, 
  SendHorizontal, Maximize2,
  Plus, X, CloudDownload, Clock, ChevronLeft, MessageSquare, RotateCcw
} from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { useCircuitBreaker } from '@/contexts/CircuitBreakerContext'
import apiClient, { acquireToken } from '@/lib/noc-api-client'
import { v4 as uuidv4 } from 'uuid'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ProposalCard from './ProposalCard'

interface SpikeResult {
  status: 'complete' | 'terminated' | 'error'
  percent: number
  downloaded_mb: number
  speed_mbps: number
  elapsed_seconds: number
  message: string
}

function SpikeResultBox({ sr, mode, t }: { sr: SpikeResult; mode: 'widget' | 'full'; t: { askNoc: { spikeDownloadComplete: string; spikeDownloadFailed: string; spikeConnectionInterrupted: string; spikeDownloadCompletedMsg: string } } }) {
  const failed = sr.status === 'terminated' || sr.status === 'error'
  return (
    <div className="flex gap-3">
      <div className={`${mode === 'widget' ? 'h-7 w-7' : 'h-8 w-8'} rounded-full bg-[#FBFBFB] flex items-center justify-center flex-shrink-0`}>
        <CloudDownload className={`${mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${failed ? 'text-red-500' : 'text-green-500'}`} />
      </div>
      <div className={`bg-[#FBFBFB] rounded-2xl ${mode === 'widget' ? 'px-3 py-2.5 text-[11px]' : 'px-4 py-3 text-[13px]'} rounded-tl-sm flex-1 max-w-[85%]`}>
        <div className="flex items-center justify-between mb-1.5">
          <span className={`font-medium ${failed ? 'text-red-600' : 'text-[#353535]'}`}>
            {sr.status === 'complete' ? t.askNoc.spikeDownloadComplete : t.askNoc.spikeDownloadFailed}
          </span>
          <span className="text-[#535EA4] font-semibold ml-2">{sr.status === 'error' ? '' : `${sr.percent}%`}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1.5">
          <div
            className={`h-1.5 rounded-full ${failed ? 'bg-red-500' : 'bg-green-500'}`}
            style={{ width: `${sr.percent}%` }}
          />
        </div>
        <div className="flex justify-between text-[#99A1AF]" style={{ fontSize: '10px' }}>
          <span>{(sr.downloaded_mb / 1024).toFixed(2)} GB downloaded</span>
          <span>{sr.speed_mbps > 0 ? `${sr.speed_mbps} Mbps` : ''}</span>
        </div>
        {failed && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-red-800" style={{ fontSize: '10px' }}>
            {sr.message || t.askNoc.spikeConnectionInterrupted}
          </div>
        )}
        {sr.status === 'complete' && (
          <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-green-800" style={{ fontSize: '10px' }}>
            {t.askNoc.spikeDownloadCompletedMsg}
          </div>
        )}
      </div>
    </div>
  )
}

interface ProposalCardType {
  approval_token: string;
  diagnosis: string;
  root_cause: string;
  confidence: string;
  steps: Array<{
    step_number: number;
    description: string;
    tool: string;
    args: Record<string, unknown>;
  }>;
  risk_level: string;
  automated_fix_possible: boolean;
  dispatcher_note?: string;
  matched_policy_id?: number;
  matched_policy_name?: string;
}

interface Message {
  id: string;
  author: 'user' | 'agent';
  content: string;
  timestamp: Date;
  raw_data?: Record<string, unknown>;
  proposal_card?: ProposalCardType;
  proposal_handled?: boolean;
}

interface AskNocManagerProps {
  mode?: 'widget' | 'full'
}

/** Detect if text contains Japanese (Hiragana, Katakana, or CJK) */
function detectLanguageFromText(text: string): 'ja' | 'en' {
  const jaRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/
  return jaRegex.test(text) ? 'ja' : 'en'
}

/** Detect if user is asking for a network spike / file download test */
function isSpikeIntent(text: string): boolean {
  const lower = (text || '').toLowerCase().trim()
  const en = /check\s*network\s*spike|network\s*spike|file\s*download|run\s*(a\s*)?network\s*spike|download\s*speed\s*test|bandwidth\s*test|spike\s*test/.test(lower)
  const ja = /例のファイル|ネットワーク\s*スパイク|スパイク\s*テスト|ダウンロード\s*テスト|帯域幅\s*テスト|ネットワークスパイク|ファイルをダウンロード/.test(text || '')
  return en || ja
}

export function AskNocManager({ mode = 'widget' }: AskNocManagerProps) {
  const router = useRouter()
  const { language, t } = useLanguage()
  const { refresh: refreshCircuitBreaker, lastStatusEvent, circuit_break_active, agents_suspended } = useCircuitBreaker()
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [conversationId, setConversationIdRaw] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('noc_active_conversation_id') || null
    }
    return null
  })

  const setConversationId = (id: string | null) => {
    setConversationIdRaw(id)
    if (typeof window !== 'undefined') {
      if (id) {
        sessionStorage.setItem('noc_active_conversation_id', id)
      } else {
        sessionStorage.removeItem('noc_active_conversation_id')
      }
    }
  }
  const [showWelcome, setShowWelcome] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  // History panel state
  const [showHistory, setShowHistory] = useState(false)
  const [historyList, setHistoryList] = useState<Array<{ id: string; title: string; updated_at: string | null }>>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Spike download progress state
  const [spikeProgress, setSpikeProgress] = useState<{
    active: boolean
    percent: number
    downloaded_mb: number
    total_mb: number
    speed_mbps: number
    elapsed_seconds: number
    status: 'downloading' | 'complete' | 'terminated' | 'error'
    message: string
  } | null>(null)

  const defaultQuestions = t.askNoc.suggestedQuestions

  // Unified questions state (default + user-added), persisted per language
  const [questions, setQuestions] = useState<string[]>(() => [...defaultQuestions])
  const [newQuestion, setNewQuestion] = useState('')

  useEffect(() => {
    const storageKey = `noc_suggested_questions_${language}`
    const saved = localStorage.getItem(storageKey)
    const defaults = t.askNoc.suggestedQuestions
    if (saved) {
      try { setQuestions(JSON.parse(saved)) } catch { setQuestions([...defaults]) }
    } else {
      setQuestions([...defaults])
    }
  }, [language, t])

  // On mount, reload the active conversation from history if the page was refreshed
  useEffect(() => {
    if (conversationId && messages.length === 0) {
      loadConversation(conversationId).catch(() => {
        // Conversation not found / not yet committed — clear stale id
        setConversationId(null)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the backend pushes "agents_resumed" via SSE, immediately unblock the
  // chat UI — no need to wait for the next poll tick.
  useEffect(() => {
    if (!lastStatusEvent) return
    if (lastStatusEvent.type === 'agents_resumed') {
      refreshCircuitBreaker()
    }
  }, [lastStatusEvent, refreshCircuitBreaker])

  const persistQuestions = (qs: string[]) => {
    setQuestions(qs)
    localStorage.setItem(`noc_suggested_questions_${language}`, JSON.stringify(qs))
  }

  const addQuestion = () => {
    const trimmed = newQuestion.trim()
    if (!trimmed || questions.includes(trimmed)) return
    persistQuestions([...questions, trimmed])
    setNewQuestion('')
  }

  const removeQuestion = (index: number) => {
    persistQuestions(questions.filter((_, i) => i !== index))
  }

  useEffect(() => {
    // Scroll within the ScrollArea container only, not the entire page
    if (scrollAreaRef.current && messagesEndRef.current) {
      // Find the Radix viewport element
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
      if (viewport) {
        // Smooth scroll to bottom within the viewport
        viewport.scrollTo({
          top: viewport.scrollHeight,
          behavior: 'smooth'
        })
      }
    }
  }, [messages])

  const markProposalHandled = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, proposal_handled: true } : msg
    ))
  }

  const addAgentMessage = (content: string) => {
    const newMsg: Message = {
      id: (Date.now() + Math.random()).toString(),
      author: 'agent',
      content,
      timestamp: new Date()
    }
    setMessages(prev => [...prev, newMsg])
  }

  const openHistory = async () => {
    setShowHistory(true)
    setHistoryLoading(true)
    try {
      const data = await apiClient('/api/chatbot/history?limit=30')
      setHistoryList(Array.isArray(data) ? data : [])
    } catch {
      setHistoryList([])
    } finally {
      setHistoryLoading(false)
    }
  }

  const loadConversation = async (id: string) => {
    try {
      const data = await apiClient(`/api/chatbot/history/${id}`)
      const loaded: Message[] = (data.messages || []).map((m: { id: string; role: string; content: string; raw_data?: Record<string, unknown>; created_at?: string }) => ({
        id: m.id,
        author: m.role === 'user' ? 'user' : 'agent',
        content: m.content,
        timestamp: m.created_at ? new Date(m.created_at) : new Date(),
        raw_data: m.raw_data ?? undefined,
      }))
      setMessages(loaded)
      setConversationId(id)
      setShowWelcome(false)
      setShowHistory(false)
    } catch {
      // silently ignore
    }
  }

  const startNewChat = () => {
    setMessages([])
    setConversationId(null)
    setShowWelcome(true)
    setShowHistory(false)
    setInputValue('')
    setSpikeProgress(null)
  }

  const handleSendMessage = async (text: string = inputValue) => {
    if (!text.trim() || isLoading) return
    
    // Hide welcome message when user sends first message
    if (showWelcome) {
      setShowWelcome(false)
    }
    
    const currentId = conversationId || uuidv4()
    if (!conversationId) setConversationId(currentId)

    const userMsg: Message = { id: Date.now().toString(), author: 'user', content: text, timestamp: new Date() }
    setMessages(prev => [...prev, userMsg])
    setInputValue('')
    setIsLoading(true)
    if (!isSpikeIntent(text)) setSpikeProgress(null)

    // For spike intents: stream the download progress live, then call chatbot for summary
    if (isSpikeIntent(text)) {
      // Block spike test when agents are suspended or circuit is broken
      if (circuit_break_active || agents_suspended) {
        const blockedMsg = circuit_break_active
          ? 'NOC Agent is currently circuit-broken. The network spike test cannot be run while agents are isolated.'
          : 'NOC Agents are currently suspended. The network spike test cannot be run while agents are suspended.'
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          author: 'agent',
          content: blockedMsg,
          timestamp: new Date()
        }])
        setIsLoading(false)
        return
      }

      setSpikeProgress({ active: true, percent: 0, downloaded_mb: 0, total_mb: 0, speed_mbps: 0, elapsed_seconds: 0, status: 'downloading', message: t.askNoc.spikeConnecting })
      try {
        // Fetch token for SSE auth
        const token = await acquireToken()
        const basePath = process.env.NEXT_PUBLIC_NOC_API_URL || (typeof window !== 'undefined' ? window.location.origin : '')
        const sseUrl = `${basePath}/cc/noc/api/chatbot/spike-test/stream?duration_seconds=180`

        const res = await fetch(sseUrl, { headers: { Authorization: `Bearer ${token ?? ''}` } })
        if (!res.ok || !res.body) throw new Error(`Spike stream unavailable (${res.status})`)

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''
        let finalMessage = ''
        let finalSpikeResult: SpikeResult | null = null

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const lines = buf.split('\n')
          buf = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const evt = JSON.parse(line.slice(6))
              if (evt.type === 'progress') {
                setSpikeProgress({ active: true, percent: evt.percent, downloaded_mb: evt.downloaded_mb, total_mb: 0, speed_mbps: evt.speed_mbps, elapsed_seconds: evt.elapsed_seconds, status: 'downloading', message: '' })
              } else if (evt.type === 'complete') {
                finalMessage = evt.message
                finalSpikeResult = {
                  status: evt.status as 'complete' | 'terminated' | 'error',
                  percent: evt.status === 'terminated' ? (evt.percent ?? 0) : 100,
                  downloaded_mb: evt.downloaded_mb,
                  speed_mbps: evt.speed_mbps,
                  elapsed_seconds: evt.duration_seconds,
                  message: evt.message,
                }
                setSpikeProgress({ active: false, ...finalSpikeResult, total_mb: 0 })
              } else if (evt.type === 'error') {
                finalMessage = evt.message
                finalSpikeResult = {
                  status: 'error',
                  percent: evt.percent ?? 0,
                  downloaded_mb: evt.downloaded_mb ?? 0,
                  speed_mbps: 0,
                  elapsed_seconds: 0,
                  message: evt.message,
                }
                setSpikeProgress({ active: false, ...finalSpikeResult, total_mb: 0 })
              }
            } catch { /* skip malformed */ }
          }
        }

        // Now call chatbot for the AI summary (download is already done)
        const response = await apiClient('/api/chatbot/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text,
            conversationId: currentId,
            chatHistory: messages.map(m => ({ role: m.author === 'agent' ? 'assistant' : 'user', content: m.content, raw_data: m.raw_data })),
            language: detectLanguageFromText(text)
          })
        })

        const newMessage: Message = {
          id: (Date.now() + 1).toString(),
          author: 'agent',
          content: response.response,
          timestamp: new Date(),
          raw_data: { ...response.raw_data, session_terminated: response.raw_data?.session_terminated === true, spike_message: finalMessage, spike_result: finalSpikeResult }
        }
        if (response.raw_data?.type === 'proposal' && response.raw_data?.proposal_card) {
          newMessage.proposal_card = response.raw_data.proposal_card as ProposalCardType
          newMessage.proposal_handled = false
        }
        setMessages(prev => [...prev, newMessage])
        setSpikeProgress(null)

      } catch {
        // Connection broke — show red bar with download failed
        const errorSpikeResult: SpikeResult = { status: 'error', percent: 100, downloaded_mb: 0, speed_mbps: 0, elapsed_seconds: 0, message: t.askNoc.spikeConnectionInterrupted }
        setSpikeProgress({ active: false, ...errorSpikeResult, total_mb: 0 })
        try {
          const response = await apiClient('/api/chatbot/message', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: text, conversationId: currentId, chatHistory: messages.map(m => ({ role: m.author === 'agent' ? 'assistant' : 'user', content: m.content })), language: detectLanguageFromText(text) })
          })
          setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), author: 'agent', content: response.response, timestamp: new Date(), raw_data: { ...response.raw_data, spike_result: errorSpikeResult } }])
          setSpikeProgress(null)
        } catch {
          setMessages(prev => [...prev, { id: 'error', author: 'agent', content: t.askNoc.errorMessage, timestamp: new Date() }])
          setSpikeProgress(null)
        }
      } finally {
        setIsLoading(false)
        refreshCircuitBreaker()
      }
      return
    }

    try {
      // Simplified API call for widget demo - reusing existing endpoint structure
      const response = await apiClient('/api/chatbot/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversationId: currentId,
          chatHistory: messages.map(m => ({ 
            role: m.author === 'agent' ? 'assistant' : 'user', 
            content: m.content,
            raw_data: m.raw_data // Preserve raw_data for context extraction
          })),
          language: detectLanguageFromText(text) // Auto-detect: Japanese input → Japanese response
        })
      })

      const newMessage: Message = {
        id: (Date.now() + 1).toString(),
        author: 'agent',
        content: response.response,
        timestamp: new Date(),
        raw_data: response.raw_data
      }

      // Check if this is a proposal response
      if (response.raw_data?.type === 'proposal' && response.raw_data?.proposal_card) {
        newMessage.proposal_card = response.raw_data.proposal_card as ProposalCardType
        newMessage.proposal_handled = false
      }

      setMessages(prev => [...prev, newMessage])
    } catch {
      setMessages(prev => [...prev, {
        id: 'error',
        author: 'agent',
        content: t.askNoc.errorMessage,
        timestamp: new Date()
      }])
    } finally {
      setIsLoading(false)
      refreshCircuitBreaker()
    }
  }

  // Format current time
  const getCurrentTime = () => {
    const now = new Date()
    return now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  }

  return (
    <Card className={`flex flex-col ${mode === 'widget' ? 'h-full rounded-xl' : 'h-full rounded-none'} bg-white overflow-hidden border border-[#DDD] shadow-none relative`}>
      {/* Header buttons */}
      <div className={`absolute top-4 z-20 flex items-center gap-1.5 ${mode === 'widget' ? 'right-4' : 'right-6'}`}>
        {/* History button */}
        <Button
          size="icon"
          variant="ghost"
          onClick={openHistory}
          className="h-8 w-8 rounded-lg bg-white/80 hover:bg-white border border-gray-200 shadow-sm"
          title="Chat history"
        >
          <Clock className="h-4 w-4 text-gray-600" />
        </Button>
        {/* New chat button */}
        <Button
          size="icon"
          variant="ghost"
          onClick={startNewChat}
          className="h-8 w-8 rounded-lg bg-white/80 hover:bg-white border border-gray-200 shadow-sm"
          title="New chat"
        >
          <RotateCcw className="h-4 w-4 text-gray-600" />
        </Button>
        {/* Maximize — only in widget mode */}
        {mode === 'widget' && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => router.push('/manager')}
            className="h-8 w-8 rounded-lg bg-white/80 hover:bg-white border border-gray-200 shadow-sm"
            title="Open in AI NOC Manager"
          >
            <Maximize2 className="h-4 w-4 text-gray-600" />
          </Button>
        )}
      </div>

      {/* History Panel Overlay */}
      {showHistory && (
        <div className="absolute inset-0 z-30 bg-white flex flex-col">
          {/* History header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EBEBEB]">
            <button
              onClick={() => setShowHistory(false)}
              className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-[#141414]" style={{ fontFamily: 'Funnel Display, sans-serif' }}>
              Chat History
            </span>
            <div className="flex-1" />
            <button
              onClick={startNewChat}
              className="flex items-center gap-1.5 text-[11px] font-medium text-[#535EA4] hover:text-[#3D4580] transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              New Chat
            </button>
          </div>

          {/* History list */}
          <div className="flex-1 overflow-y-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-[#9C9C9C]">
                <span className="animate-pulse">Loading history...</span>
              </div>
            ) : historyList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#9C9C9C]">
                <MessageSquare className="h-8 w-8 opacity-30" />
                <span className="text-sm">No previous conversations</span>
              </div>
            ) : (
              <ul className="divide-y divide-[#F4F4F4]">
                {historyList.map((conv) => {
                  const date = conv.updated_at ? new Date(conv.updated_at) : null
                  const dateLabel = date
                    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                    : ''
                  return (
                    <li key={conv.id}>
                      <button
                        onClick={() => loadConversation(conv.id)}
                        className="w-full text-left px-4 py-3 hover:bg-[#F7F8FF] transition-colors flex items-start gap-3 group"
                      >
                        <div className="h-7 w-7 rounded-full bg-[#F0F2FC] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Bot className="h-3.5 w-3.5 text-[#535EA4]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-[#353535] truncate group-hover:text-[#535EA4] transition-colors" style={{ fontFamily: 'Funnel Display, sans-serif' }}>
                            {conv.title}
                          </p>
                          {dateLabel && (
                            <p className="text-[11px] text-[#9C9C9C] mt-0.5">{dateLabel}</p>
                          )}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Blue gradient background - Semi-circular shape visible at bottom */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {/* Large radial gradient positioned at bottom for prominent blue effect */}
        <div 
          className="absolute left-1/2 -translate-x-1/2 bottom-0"
          style={{
            width: '150%',
            height: '80%',
            background: 'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(138, 162, 223, 0.5) 0%, rgba(160, 180, 230, 0.4) 20%, rgba(180, 195, 235, 0.3) 35%, rgba(200, 210, 242, 0.2) 50%, rgba(220, 225, 248, 0.1) 70%, transparent 100%)',
            filter: 'blur(60px)'
          }}
        />
      </div>
      {/* Subtle white overlay */}
      <div className="absolute bg-white opacity-15 inset-0 pointer-events-none z-[1]" />

      {/* Chat Messages Area - with proper top padding and bottom padding for input field */}
      <div className={`flex-1 min-h-0 ${mode === 'widget' ? 'px-[20px] pt-[20px]' : 'px-0 pt-0'} pb-0 relative z-10`}>
        <ScrollArea ref={scrollAreaRef} className="h-full">
          <div className={`${mode === 'full' ? 'max-w-3xl mx-auto px-6 pt-6' : ''} space-y-4 py-0 ${mode === 'widget' ? 'pb-[148px]' : 'pb-[200px]'}`}>
            {/* Welcome Message with Suggested Questions - Figma Design */}
            {showWelcome && messages.length === 0 && (
              <div className="flex gap-3">
                {/* Avatar with gradient background matching Figma */}
                <div className={`${mode === 'widget' ? 'h-8 w-8' : 'h-10 w-10'} rounded-lg flex items-center justify-center flex-shrink-0 relative overflow-hidden`}>
                  <div 
                    className="absolute inset-0 rotate-[-90deg]"
                    style={{
                      background: 'linear-gradient(134.85deg, #E7E7E7 21.60%, #7E57C5 94%)',
                      borderRadius: '2.4px'
                    }}
                  />
                  <div 
                    className="absolute inset-0 rotate-[-90deg] scale-y-[-1]"
                    style={{
                      mixBlendMode: 'screen'
                    }}
                  >
                    <div className="w-full h-full opacity-30" />
                  </div>
                  <Sparkles className={`${mode === 'widget' ? 'h-4 w-4' : 'h-5 w-5'} text-white relative z-10`} />
                </div>

                {/* Welcome Container */}
                <div className={`flex-1 bg-[rgba(255,255,255,0.6)] rounded-2xl ${mode === 'widget' ? 'p-4 space-y-4' : 'p-6 space-y-6'}`}>
                  {/* Welcome Text */}
                  <div className={mode === 'widget' ? 'space-y-2' : 'space-y-3'}>
                    <h3 className={`${mode === 'widget' ? 'text-[14px] leading-[20px]' : 'text-[16px] leading-[24px]'} font-normal tracking-[-0.3125px] text-[#353535]`}>
                      {t.askNoc.welcomeTitle}
                    </h3>
                    <p className={`${mode === 'widget' ? 'text-[12px] leading-[18px]' : 'text-[14px] leading-[20px]'} font-normal tracking-[-0.1504px] text-[#364153]`}>
                      {t.askNoc.welcomeText}
                    </p>
                    
                    <p className={`${mode === 'widget' ? 'text-[10px] leading-[14px] pt-1' : 'text-[12px] leading-[16px] pt-2'} font-normal text-[#99A1AF]`}>
                      {getCurrentTime()}
                    </p>
                  </div>

                </div>
              </div>
            )}

            {messages.map((msg) => (
              <React.Fragment key={msg.id}>
              <div className={`flex gap-3 ${msg.author === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`${mode === 'widget' ? 'h-7 w-7' : 'h-8 w-8'} rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.author === 'user' ? 'bg-[#535EA4] text-white' : 'bg-[#FBFBFB] text-[#535EA4]'
                }`}>
                  {msg.author === 'user' ? <User className={mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} /> : <Bot className={mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />}
                </div>
                <div className={`rounded-2xl ${mode === 'widget' ? 'px-3 py-2' : 'px-5 py-4'} ${mode === 'widget' ? 'max-w-[85%]' : 'max-w-[92%]'} ${
                  msg.author === 'user' 
                    ? `bg-[#535EA4] text-white ${mode === 'widget' ? 'text-[12px]' : 'text-[14px]'} rounded-tr-sm` 
                    : `bg-[#FBFBFB] text-[#353535] ${mode === 'widget' ? 'text-[12px]' : 'text-[14px]'} rounded-tl-sm`
                }`}>
                  {msg.author === 'user' ? (
                    msg.content
                  ) : (
                    <>
                      {msg.raw_data?.session_terminated === true && (
                        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-xs">
                          {t.askNoc.spikeSessionTerminatedCallout}
                        </div>
                      )}
                      <div className="markdown-content">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            // Style links
                            a: ({...props}) => (
                              <a {...props} className="text-[#535EA4] underline hover:text-[#454C7C]" target="_blank" rel="noopener noreferrer" />
                            ),
                            // Style tables
                            table: ({...props}) => (
                              <div className="overflow-x-auto my-2">
                                <table {...props} className={`min-w-full border-collapse border border-gray-300 ${mode === 'widget' ? 'text-[10px]' : 'text-xs'}`} />
                              </div>
                            ),
                            thead: ({...props}) => (
                              <thead {...props} className="bg-[#F5F6F7]" />
                            ),
                            th: ({...props}) => (
                              <th {...props} className="border border-gray-300 px-2 py-1 text-left font-semibold" />
                            ),
                            td: ({...props}) => (
                              <td {...props} className="border border-gray-300 px-2 py-1" />
                            ),
                            // Style code blocks
                            code: ({className, ...props}) => {
                              const isInline = !className?.includes('language-')
                              return isInline ? 
                                <code {...props} className={`bg-gray-200 px-1 py-0.5 rounded ${mode === 'widget' ? 'text-[10px]' : 'text-[13px]'}`} /> :
                                <code {...props} className={`block bg-gray-800 text-green-300 ${mode === 'widget' ? 'p-2 text-[10px]' : 'p-4 text-[13px] leading-relaxed'} rounded-lg my-3 overflow-x-auto font-mono whitespace-pre`} />
                            },
                            // Style lists
                            ul: ({...props}) => (
                              <ul {...props} className="list-disc list-inside my-2 space-y-1" />
                            ),
                            ol: ({...props}) => (
                              <ol {...props} className="list-decimal list-inside my-2 space-y-1" />
                            ),
                            // Style paragraphs
                            p: ({...props}) => (
                              <p {...props} className="my-1" />
                            ),
                            // Style headings
                            h1: ({...props}) => (
                              <h1 {...props} className={`${mode === 'widget' ? 'text-base' : 'text-lg'} font-bold my-2`} />
                            ),
                            h2: ({...props}) => (
                              <h2 {...props} className={`${mode === 'widget' ? 'text-sm' : 'text-base'} font-bold my-2`} />
                            ),
                            h3: ({...props}) => (
                              <h3 {...props} className={`${mode === 'widget' ? 'text-xs' : 'text-sm'} font-bold my-1`} />
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                      
                      {/* Render ProposalCard if present and not handled */}
                      {msg.proposal_card && !msg.proposal_handled && conversationId && (
                        <ProposalCard
                          proposal={msg.proposal_card}
                          conversationId={conversationId}
                          chatHistory={messages.map(m => ({ 
                            role: m.author === 'agent' ? 'assistant' : 'user', 
                            content: m.content,
                            raw_data: m.raw_data
                          }))}
                          language={language}
                          onApproved={(result) => {
                            addAgentMessage(result)
                            markProposalHandled(msg.id)
                          }}
                          onCancelled={() => {
                            addAgentMessage("Plan cancelled. Let me know if you'd like to try a different approach.")
                            markProposalHandled(msg.id)
                          }}
                          onEdit={(query) => setInputValue(query)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
              {/* Spike result box — rendered from raw_data so it persists in history */}
              {msg.author === 'agent' && msg.raw_data?.spike_result != null && (
                <SpikeResultBox sr={msg.raw_data.spike_result as SpikeResult} mode={mode} t={t} />
              )}
              </React.Fragment>
            ))}
            {/* Spike download progress bar */}
            {spikeProgress && (
              <div className="flex gap-3">
                <div className={`${mode === 'widget' ? 'h-7 w-7' : 'h-8 w-8'} rounded-full bg-[#FBFBFB] flex items-center justify-center flex-shrink-0`}>
                  <CloudDownload className={`${mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${spikeProgress.active ? 'text-[#535EA4] animate-bounce' : (spikeProgress.status === 'terminated' || spikeProgress.status === 'error') ? 'text-red-500' : 'text-green-500'}`} />
                </div>
                <div className={`bg-[#FBFBFB] rounded-2xl ${mode === 'widget' ? 'px-3 py-2.5 text-[11px]' : 'px-4 py-3 text-[13px]'} rounded-tl-sm flex-1 max-w-[85%]`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`font-medium ${(spikeProgress.status === 'terminated' || spikeProgress.status === 'error') ? 'text-red-600' : 'text-[#353535]'}`}>
                      {spikeProgress.active ? t.askNoc.spikeDownloading : spikeProgress.status === 'complete' ? t.askNoc.spikeDownloadComplete : t.askNoc.spikeDownloadFailed}
                    </span>
                    <span className="text-[#535EA4] font-semibold ml-2">{spikeProgress.status === 'error' ? '' : `${spikeProgress.percent}%`}</span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${(spikeProgress.status === 'terminated' || spikeProgress.status === 'error') ? 'bg-red-500' : spikeProgress.status === 'complete' ? 'bg-green-500' : 'bg-[#535EA4]'}`}
                      style={{ width: `${spikeProgress.percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[#99A1AF]" style={{ fontSize: '10px' }}>
                    <span>{(spikeProgress.downloaded_mb / 1024).toFixed(2)} GB downloaded</span>
                    <span>{spikeProgress.speed_mbps > 0 ? `${spikeProgress.speed_mbps} Mbps` : ''}</span>
                  </div>
                  {!spikeProgress.active && (spikeProgress.status === 'terminated' || spikeProgress.status === 'error') && (
                    <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-red-800" style={{ fontSize: '10px' }}>
                      {spikeProgress.message || t.askNoc.spikeConnectionInterrupted}
                    </div>
                  )}
                  {!spikeProgress.active && spikeProgress.status === 'complete' && (
                    <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-green-800" style={{ fontSize: '10px' }}>
                      {t.askNoc.spikeDownloadCompletedMsg}
                    </div>
                  )}
                </div>
              </div>
            )}

            {isLoading && !spikeProgress && (
              <div className="flex gap-3">
                <div className={`${mode === 'widget' ? 'h-7 w-7' : 'h-8 w-8'} rounded-full bg-[#FBFBFB] flex items-center justify-center`}>
                  {messages.length > 0 && messages[messages.length - 1]?.author === 'user' && isSpikeIntent(messages[messages.length - 1].content) ? (
                    <CloudDownload className={`${mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-[#535EA4] animate-pulse`} />
                  ) : (
                    <Bot className={`${mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-[#535EA4] animate-pulse`} />
                  )}
                </div>
                <div className={`bg-[#FBFBFB] text-[#353535] rounded-2xl ${mode === 'widget' ? 'px-3 py-2 text-[12px]' : 'px-4 py-3 text-[14px]'} flex items-center gap-2`}>
                  <span className="inline-block w-2 h-2 rounded-full bg-[#535EA4] animate-pulse" />
                  <span className="animate-pulse">
                    {messages.length > 0 && messages[messages.length - 1]?.author === 'user' && isSpikeIntent(messages[messages.length - 1].content)
                      ? t.askNoc.spikeDownloadingForTest
                      : t.askNoc.fetchingData}
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Always-visible Suggested Questions Strip */}
      <div className={`absolute ${mode === 'widget' ? 'bottom-[104px] left-[20px] right-[20px]' : 'bottom-[136px] left-1/2 -translate-x-1/2 w-full max-w-3xl px-6'} z-20 flex gap-2 flex-wrap`}>
        {questions.map((q, idx) => (
          <div
            key={idx}
            className={`flex items-center gap-1 bg-white/80 border border-[#E5E7EB] rounded-[24px] ${mode === 'widget' ? 'pl-2.5 pr-1.5 py-0.5' : 'pl-3 pr-2 py-1'} hover:bg-white transition-colors shadow-sm`}
          >
            <button
              onClick={() => handleSendMessage(q)}
              className={`${mode === 'widget' ? 'text-[11px] leading-[16px]' : 'text-[13px] leading-[18px]'} font-normal text-[#364153] hover:text-[#535EA4] transition-colors text-left max-w-[200px] truncate`}
              title={q}
            >
              {q}
            </button>
            <button
              onClick={() => removeQuestion(idx)}
              className="ml-0.5 text-[#D1D5DC] hover:text-red-400 transition-colors flex-shrink-0"
              title="Remove"
            >
              <X className={`${mode === 'widget' ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
            </button>
          </div>
        ))}

        {/* Inline add input pill */}
        <div className={`flex items-center gap-1 bg-white/80 border border-dashed border-[#D1D5DC] rounded-[24px] ${mode === 'widget' ? 'pl-2.5 pr-1.5 py-0.5' : 'pl-3 pr-2 py-1'} shadow-sm`}>
          <input
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addQuestion()}
            placeholder={t.askNoc.addQuestion}
            className={`bg-transparent outline-none ${mode === 'widget' ? 'text-[10px] w-[80px]' : 'text-[12px] w-[110px]'} text-[#364153] placeholder:text-[#C4C9D4]`}
          />
          <button
            onClick={addQuestion}
            disabled={!newQuestion.trim()}
            className="text-[#C4C9D4] hover:text-[#535EA4] disabled:opacity-40 transition-colors flex-shrink-0"
            title="Add"
          >
            <Plus className={`${mode === 'widget' ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
          </button>
        </div>
      </div>

      {/* Input Area - With proper glassmorphism effect */}
      <div className={`absolute ${mode === 'widget' ? 'bottom-[20px] left-[20px] right-[20px] h-[76px]' : 'bottom-[28px] left-1/2 -translate-x-1/2 w-full max-w-3xl px-6 h-[92px]'} z-10`}>
        <div className={`relative w-full h-full ${mode === 'widget' ? 'rounded-[20px]' : 'rounded-[24px]'}`}>
          {/* Outer glassmorphism container - highly visible white border and blur */}
          <div 
            className={`absolute inset-0 ${mode === 'widget' ? 'rounded-[20px]' : 'rounded-[24px]'}`}
            style={{
              background: 'linear-gradient(180deg, rgba(85, 85, 85, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: mode === 'widget' ? '2px solid rgba(255, 255, 255, 0.8)' : '3px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          />
          {/* Inner white input container */}
          <div 
            className={`absolute bg-white border border-[#E5E7EB] ${mode === 'widget' ? 'h-[56px] rounded-[16px]' : 'h-[68px] rounded-[20px]'} left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-3 ${mode === 'widget' ? 'px-3' : 'px-4'} shadow-md`}
            style={{ width: mode === 'widget' ? 'calc(100% - 20px)' : 'calc(100% - 24px)' }}
          >
            {/* Input field */}
            <Input 
              placeholder={mode === 'widget' ? t.askNoc.placeholder : t.askNoc.placeholderFull} 
              className={`flex-1 border-0 bg-transparent text-[#353535] ${mode === 'widget' ? 'text-[13px]' : 'text-[16px]'} placeholder:text-[#9C9C9C] focus-visible:ring-0 focus-visible:ring-offset-0 font-normal tracking-[-0.32px] p-0 h-auto`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            
            {/* Send Button */}
            <Button 
              size="icon" 
              className={`${mode === 'widget' ? 'h-[32px] w-[32px] rounded-[10px]' : 'h-[36px] w-[36px] rounded-[12px]'} bg-[#141A42] hover:bg-[#0F1431] shadow-none border-0 flex-shrink-0`}
              onClick={() => handleSendMessage()}
            >
              <SendHorizontal className={`${mode === 'widget' ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-[#FDFDFD]`} />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}
